import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import {
  Coin, Lightning, Check, ArrowRight, Trophy, Sparkle, CurrencyEur, Receipt, Gift, EnvelopeSimple, Users, Share,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Visual themes per pack — keyed by pack id
const PACK_THEMES = {
  starter:  { gradient: 'from-[#2563EB]/15 to-transparent', accent: '#2563EB', icon: Coin },
  pro:      { gradient: 'from-[#A855F7]/18 to-transparent', accent: '#A855F7', icon: Lightning, popular: true },
  power:    { gradient: 'from-[#F59E0B]/18 to-transparent', accent: '#F59E0B', icon: Sparkle },
  ultimate: { gradient: 'from-[#10B981]/18 to-transparent', accent: '#10B981', icon: Trophy, bestValue: true },
};

const PackCard = ({ pack, onBuy, busy, t }) => {
  const theme = PACK_THEMES[pack.id] || PACK_THEMES.starter;
  const Icon = theme.icon;
  const perCredit = (pack.price_eur / pack.credits).toFixed(2);
  return (
    <div className={`relative p-6 bg-gradient-to-br ${theme.gradient} bg-[#0F0F0F] border border-white/10 rounded-2xl flex flex-col`}
      style={{ borderColor: theme.popular || theme.bestValue ? `${theme.accent}66` : undefined }}
      data-testid={`pack-card-${pack.id}`}>
      {(theme.popular || theme.bestValue) && (
        <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded text-[10px] uppercase tracking-[0.2em] font-bold"
          style={{ background: theme.accent, color: '#fff' }}>
          {theme.popular ? t('credits.most_popular') : t('credits.best_value')}
        </div>
      )}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">{pack.label}</div>
          <div className="text-3xl font-[Outfit] font-bold mt-1" style={{ color: theme.accent }}>
            {pack.credits} <span className="text-base text-white/60 font-normal">{t('credits.credits_word')}</span>
          </div>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${theme.accent}22` }}>
          <Icon size={18} weight="bold" style={{ color: theme.accent }} />
        </div>
      </div>
      <p className="text-xs text-white/55 mb-4">{pack.best_for}</p>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-3xl font-[Outfit] font-bold">€{pack.price_eur}</span>
        <span className="text-xs text-white/40">{t('credits.one_time')}</span>
      </div>
      <div className="text-[11px] text-white/40 mb-5">{t('credits.per_credit', { v: perCredit })}</div>
      <ul className="space-y-1.5 mb-6 text-sm">
        <li className="flex items-start gap-2"><Check size={12} weight="bold" className="text-[#10B981] flex-shrink-0 mt-1.5" /><span className="text-white/80">{t('credits.feat_generations', { n: pack.credits })}</span></li>
        <li className="flex items-start gap-2"><Check size={12} weight="bold" className="text-[#10B981] flex-shrink-0 mt-1.5" /><span className="text-white/80">{t('credits.feat_no_expiry')}</span></li>
        <li className="flex items-start gap-2"><Check size={12} weight="bold" className="text-[#10B981] flex-shrink-0 mt-1.5" /><span className="text-white/80">{t('credits.feat_features')}</span></li>
      </ul>
      <button data-testid={`btn-buy-${pack.id}`} onClick={() => onBuy(pack.id)} disabled={busy}
        className="mt-auto px-4 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition disabled:opacity-50"
        style={{ background: theme.accent, color: '#fff' }}>
        {busy === pack.id ? t('credits.redirecting') : <>{t('credits.buy')} <ArrowRight size={12} weight="bold" /></>}
      </button>
    </div>
  );
};

const TxnRow = ({ txn, t }) => {
  const isGiftSent = txn.kind === 'gift_sent';
  const isRedeem = txn.kind === 'gift_redeem';
  const isHallBonus = txn.kind === 'hall_bonus';
  const positive = (txn.delta || 0) > 0;
  const label = isGiftSent
    ? `${t('credits.txn_gift_sent')} · ${txn.pack_id || '—'} → ${txn.recipient_email || '—'}`
    : isRedeem
    ? `${t('credits.txn_gift_received')} · ${txn.gift_code || '—'}`
    : isHallBonus
    ? t('credits.txn_hall_bonus')
    : txn.kind === 'purchase'
    ? `${t('credits.txn_purchase')} · ${txn.pack_id || '—'}`
    : `${t('credits.txn_usage')} · ${txn.feature || '—'}`;
  const deltaDisplay = isGiftSent
    ? '🎁'
    : `${positive ? '+' : ''}${txn.delta}`;
  const color = isGiftSent ? '#A855F7' : positive ? '#10B981' : '#EF4444';
  return (
    <tr className="border-b border-white/5 last:border-0">
      <td className="py-2.5">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[10px] text-white/40 font-mono">{new Date(txn.created_at).toLocaleString()}</div>
      </td>
      <td className="py-2.5 text-right">
        <span className="font-bold text-lg" style={{ color }}>{deltaDisplay}</span>
      </td>
    </tr>
  );
};

export default function Credits() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [giftMode, setGiftMode] = useState(false);
  const [giftForm, setGiftForm] = useState({ recipient_email: '', gifter_name: '', message: '' });
  const [referrals, setReferrals] = useState(null);

  const load = useCallback(async () => {
    try {
      const [packsRes, balanceRes, refRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/credits/packs`),
        axios.get(`${BACKEND_URL}/api/credits/balance`),
        axios.get(`${BACKEND_URL}/api/share/me/referrals`).catch(() => ({ data: null })),
      ]);
      setPacks(packsRes.data.packs || []);
      setBalance(balanceRes.data.balance || 0);
      setTxns(balanceRes.data.transactions || []);
      setReferrals(refRes.data);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(t('credits.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    // Stripe success callback — reload to pick up the new balance
    if (params.get('purchase') === 'success') {
      toast.success(t('credits.payment_confirmed'));
      // Poll for 30s — webhook may take a moment
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${BACKEND_URL}/api/credits/balance`);
          if (data.balance !== balance) {
            setBalance(data.balance);
            setTxns(data.transactions || []);
            clearInterval(poll);
          }
        } catch { /* ignore */ }
        if (attempts >= 10) clearInterval(poll);
      }, 3000);
      return () => clearInterval(poll);
    }
    if (params.get('purchase') === 'cancelled') {
      toast.info(t('credits.purchase_cancelled'));
    }
  }, [load, params, balance, t]);

  const buyPack = async (packId) => {
    if (giftMode && !giftForm.recipient_email.trim()) {
      toast.error(t('credits.missing_recipient'));
      return;
    }
    setBusy(packId);
    try {
      const body = {
        pack_id: packId,
        origin_url: window.location.origin,
      };
      if (giftMode) {
        body.recipient_email = giftForm.recipient_email.trim();
        body.gifter_name = giftForm.gifter_name.trim() || null;
        body.message = giftForm.message.trim() || null;
      }
      const { data } = await axios.post(`${BACKEND_URL}/api/credits/checkout`, body);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(t('credits.checkout_missing_url'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('credits.checkout_failed'));
      setBusy(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-3">
              <Coin size={14} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#F59E0B] font-semibold">{t('credits.kicker')}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">{t('credits.title')}</h1>
            <p className="text-white/60 text-sm mt-1 max-w-2xl">
              {t('credits.subtitle')}
            </p>
          </div>
          <div className="p-5 bg-gradient-to-br from-[#F59E0B]/15 to-transparent border border-[#F59E0B]/35 rounded-2xl text-right min-w-[200px]" data-testid="credit-balance-widget">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-semibold mb-1">{t('credits.balance_label')}</div>
            <div className="text-4xl font-[Outfit] font-bold" data-testid="credit-balance-value">{loading ? '—' : balance}</div>
            <div className="text-xs text-white/50 mt-1">{t('credits.balance_sub')}</div>
          </div>
        </div>

        {/* Referrals widget — only renders if user already shared at least 1 battle */}
        {referrals?.links?.length > 0 && (
          <div className="p-5 bg-gradient-to-r from-[#A855F7]/10 via-[#3B82F6]/5 to-transparent border border-[#A855F7]/30 rounded-2xl" data-testid="referrals-widget">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] font-semibold mb-1 flex items-center gap-2">
                  <Users size={11} weight="bold" /> {t('credits.referrals_kicker')}
                </div>
                <div className="text-base font-[Outfit] font-bold">
                  <Trans i18nKey="credits.referrals_title">
                    Earn <span className="text-[#A855F7]">10 credits</span> for every Pro signup via Battle Card
                  </Trans>
                </div>
              </div>
              <a
                href="/heatmap"
                className="px-4 py-2 bg-[#A855F7]/20 hover:bg-[#A855F7]/30 text-[#C084FC] text-xs font-semibold rounded-lg flex items-center gap-2 transition-all border border-[#A855F7]/40"
                data-testid="referrals-share-btn"
              >
                <Share size={12} weight="bold" />
                {t('credits.referrals_share')}
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-[#0A0A0A]/60 rounded-lg" data-testid="referrals-stat-credits">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('credits.stat_credits_earned')}</div>
                <div className="text-xl font-bold text-[#10B981]">+{referrals.totals.total_credits_earned}</div>
              </div>
              <div className="p-3 bg-[#0A0A0A]/60 rounded-lg" data-testid="referrals-stat-conversions">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('credits.stat_pro_conversions')}</div>
                <div className="text-xl font-bold text-[#A855F7]">{referrals.totals.successful_referrals}</div>
              </div>
              <div className="p-3 bg-[#0A0A0A]/60 rounded-lg" data-testid="referrals-stat-claims">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('credits.stat_signups')}</div>
                <div className="text-xl font-bold text-[#3B82F6]">{referrals.totals.total_claims}</div>
              </div>
              <div className="p-3 bg-[#0A0A0A]/60 rounded-lg" data-testid="referrals-stat-views">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t('credits.stat_views')}</div>
                <div className="text-xl font-bold text-white">{referrals.totals.total_views}</div>
              </div>
            </div>
          </div>
        )}

        {/* Mode toggle + Redeem link */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex p-1 bg-[#0F0F0F] border border-white/10 rounded-xl" data-testid="mode-toggle">
            <button data-testid="btn-mode-self"
              onClick={() => setGiftMode(false)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${!giftMode ? 'bg-[#F59E0B] text-[#1a0e00]' : 'text-white/60 hover:text-white'}`}>
              {t('credits.mode_self')}
            </button>
            <button data-testid="btn-mode-gift"
              onClick={() => setGiftMode(true)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-1.5 ${giftMode ? 'bg-[#A855F7] text-white' : 'text-white/60 hover:text-white'}`}>
              <Gift size={14} weight="bold" /> {t('credits.mode_gift')}
            </button>
          </div>
          <a href="/credits/redeem" data-testid="link-redeem"
            className="text-sm text-white/60 hover:text-[#F59E0B] flex items-center gap-1.5 transition">
            <Gift size={14} weight="bold" /> {t('credits.redeem_link')}
          </a>
        </div>

        {giftMode && (
          <div className="p-5 bg-gradient-to-br from-[#A855F7]/10 to-transparent border border-[#A855F7]/30 rounded-2xl" data-testid="gift-form">
            <div className="flex items-center gap-2 mb-3">
              <Gift size={16} weight="bold" className="text-[#A855F7]" />
              <div className="font-[Outfit] font-bold">{t('credits.gift_details')}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1"><EnvelopeSimple size={10} /> {t('credits.gift_recipient')}</label>
                <input data-testid="input-recipient-email" type="email" value={giftForm.recipient_email}
                  onChange={(e) => setGiftForm({ ...giftForm, recipient_email: e.target.value })}
                  placeholder={t('credits.gift_recipient_ph')} required
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50">{t('credits.gift_name')}</label>
                <input data-testid="input-gifter-name" value={giftForm.gifter_name}
                  onChange={(e) => setGiftForm({ ...giftForm, gifter_name: e.target.value })}
                  placeholder={t('credits.gift_name_ph')}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7]" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-white/50">{t('credits.gift_message')}</label>
                <textarea data-testid="input-gift-message" value={giftForm.message}
                  onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value.slice(0, 280) })}
                  placeholder={t('credits.gift_message_ph')}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#A855F7] resize-none" />
                <div className="text-[10px] text-white/40 mt-1 text-right">{giftForm.message.length}/280</div>
              </div>
            </div>
            <p className="text-xs text-white/55 mt-3 pt-3 border-t border-white/5">
              {t('credits.gift_note')}
            </p>
          </div>
        )}

        {/* Pack grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packs.map((p) => (
            <PackCard
              key={p.id}
              pack={{ ...p, best_for: i18n.language?.startsWith('en') ? (p.best_for_en || p.best_for) : p.best_for }}
              onBuy={buyPack}
              busy={busy}
              t={t}
            />
          ))}
          {loading && [1,2,3,4].map((i) => (
            <div key={i} className="h-72 bg-[#0F0F0F] border border-white/10 rounded-2xl animate-pulse" />
          ))}
        </div>

        {/* Transactions history */}
        {txns.length > 0 && (
          <div className="p-5 bg-[#121212] border border-white/10 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Receipt size={16} weight="bold" className="text-white/60" />
              <h3 className="font-[Outfit] font-bold">{t('credits.history_title')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-white/10">
                    <th className="pb-2">{t('credits.txn_col')}</th>
                    <th className="pb-2 text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((txn, i) => <TxnRow key={i} txn={txn} t={t} />)}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FAQ-style explainer */}
        <div className="p-5 bg-[#0A0A0A] border border-white/10 rounded-2xl">
          <h3 className="font-[Outfit] font-bold mb-3 flex items-center gap-2">
            <CurrencyEur size={16} weight="bold" className="text-[#A855F7]" />
            {t('credits.how_title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-[10px] uppercase tracking-wider text-[#2563EB] font-bold mb-1">{t('credits.how1_t')}</div>
              <p className="text-white/75">{t('credits.how1_p')}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-[10px] uppercase tracking-wider text-[#A855F7] font-bold mb-1">{t('credits.how2_t')}</div>
              <p className="text-white/75">{t('credits.how2_p')}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-[10px] uppercase tracking-wider text-[#10B981] font-bold mb-1">{t('credits.how3_t')}</div>
              <p className="text-white/75">{t('credits.how3_p')}</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
